import { useEffect, useState } from 'react';
import { Input as AntdInput } from 'antd';
import styled from 'styled-components';
import { BorderedField } from './Flex';
import { ClipIcon } from '../icons/ClipIcon';
import { CancelIcon } from '../icons/CancelIcon';
import { Paragraph } from './Text';
import { ExplainIcon } from '../icons/ExplainIcon';
import { FormTooltip } from './Tooltip';
import { CodeIcon } from '../icons/CodeIcon';
import { EyeIcon } from '../icons/EyeIcon';
import { EyeInvisibleIcon } from '../icons/EyeInvisibleIcon';


const StyledInput = styled(AntdInput).attrs({
    bordered: false
})`
    height: ${({ height}) => height ? height : "32px"};
    font-size: 14px;
    line-height: 19px;
    color: #163466;
    text-transform: ${({ uppercase }) => uppercase && 'uppercase'};
    border-radius: 8px;

    &[disabled] {
        background: var(--sp-disabled-color);
    }
`;

const StyledTextArea = styled(AntdInput.TextArea).attrs(({ rows=3 }) => ({
    rows: rows,
    autoSize: { minRows: rows, maxRows: rows },
    bordered: false
}))`
    color: var(--sp-text-color);
`;


export const Input = ({ inputRef, ...props}) => (
    props.bordered
        ? <BorderedField>
            <StyledInput {...props} />
        </BorderedField>
        : <StyledInput ref={inputRef} {...props} />
);

Input.TextArea = (props) => (
    props.bordered
        ? <BorderedField {...props}>
            <StyledTextArea {...props} />
        </BorderedField>
        : <StyledTextArea {...props} />
    );

const StyledPasswordIcon = styled.span`
    margin-right: 11px;
    cursor: pointer;
    &:active > i {
        transform: translateY(1px);
    }
`
export const InputPassword = (props) => {
    const [visible, setVisible] = useState(false);

    return(
        <>
            <StyledInput type={visible ? "text" : "password"} {...props}/>
            <StyledPasswordIcon onClick={() => setVisible(!visible)} >
                {visible ? <EyeIcon /> : <EyeInvisibleIcon />}
            </StyledPasswordIcon>
        </>
    )
};

const StyledInputFileLabel = styled.label`
    padding: ${({ padding }) => padding};
    flex: 1;
    display: flex;
    align-items: center;
    width: calc(100% - 20px);
    border-radius: 8px;
    & > i {
        color: #E4EDFD;
    }

    &:hover > i, &:focus > i {
        color: #d2e1fc;
    }

    &[disabled] {
        cursor: not-allowed;
    }
`;

const StyledInputIcon = styled.span`
    padding: ${({ padding }) => padding};
    flex: 1;
    display: flex;
    align-items: center;
    width: calc(100% - 20px);
    border-radius: 8px;
    & > i {
        color: #E4EDFD;
    }

    &:hover > i, &:focus > i {
        color: #d2e1fc;
}

&[disabled] {
    cursor: not-allowed;
}
`

const StyledInputFile = styled.input.attrs({
    type: "file",
    onClick: (e) => { e.target.value = null }
})`
    display: none;
`;

export const InputFile = ({ selected, onCancel, error, ...props}) => (
    <BorderedField hasError={typeof error == "string"} padding={{ top: "4px", bottom: "4px", left: "10px", right: "10px" }}>
        <StyledInputFileLabel disabled={props.disabled}>
            <ClipIcon disabled={props.disabled} style={{ marginRight: "8px" }} />
            {selected
            ? <Paragraph title={selected.name} nowrap disabled={props.disabled}>{selected.name}</Paragraph>
            : <Paragraph blur>Browse file...</Paragraph>}
            <StyledInputFile {...props}/>
        </StyledInputFileLabel>
        {selected && <CancelIcon size={20} onClick={() => !props.disabled && onCancel()} {...props} />}
        {error &&
            <>
                <FormTooltip text={error}>
                    <ExplainIcon size={16} />
                </FormTooltip>
            </>}
    </BorderedField>
);

const IssueComment = ({ text, onTextChange, pingServer, formatCode, onTextSelect }) => {
    useEffect(() => {
        let interval;
        if (text) {
            interval = setInterval(() => {
                pingServer();
            }, 300000)
        };

        return () => clearInterval(interval);
    }, [ text ]);

    return (
        <BorderedField align="start">
            <StyledInputIcon padding="6px 0px 0px 6px" title="Code snippet">
                <CodeIcon onClick={() => formatCode()}/>
            </StyledInputIcon>
            <Input.TextArea
                autoFocus
                value={text}
                onChange={(e) => {
                    e.persist();
                    onTextChange(e.target.value);
                }}
                onSelect={onTextSelect}
            />
        </BorderedField>
    )
};

const IssueAttachment = ({ disabled, onFileChange, ...props }) => (
    <StyledInputFileLabel disabled={disabled} padding="6px 0px 0px 6px" title="Attach a file">
        {props.children}
        <StyledInputFile
            name="file"
            disabled={disabled}
            onChange={(e) => {
                e.persist();
                if (e.target && e.target.files) onFileChange(e.target.files[0]);
            }}
        />
    </StyledInputFileLabel>
)

export { IssueComment, IssueAttachment }